import { SelectableItem } from "../components/action-modals/item-selection/item-row";
import { VisibilityType } from "../components/posts/visibility-modal/post-visibility-modal";

export type PostAuthor = {
  id: string;
  display_name: string | null;
};

export type PostFilePayload = {
  attachment_id?: string;
  uri: string;
  fileName: string;
  type?: string;
  file?: File;
  file_size: number;
};

/**
 * Reactions. `asset` is the animated Noto Emoji (Google, CC BY 4.0) WebP; the
 * text `emoji` is the fallback. Unknown ids coming from newer clients are
 * ignored by older ones, so new entries only ever get appended.
 */
export const REACTIONS = [
  { id: "like", label: "Like", emoji: "👍", color: "#1877F2", asset: require("../assets/reactions/like.webp") },
  { id: "heart", label: "Heart", emoji: "❤️", color: "#F33E58", asset: require("../assets/reactions/heart.webp") },
  { id: "laugh", label: "Laugh", emoji: "😂", color: "#F7B125", asset: require("../assets/reactions/laugh.webp") },
  { id: "sad", label: "Sad", emoji: "😢", color: "#F7B125", asset: require("../assets/reactions/sad.webp") },
  { id: "mad", label: "Mad", emoji: "😡", color: "#E9710F", asset: require("../assets/reactions/mad.webp") },
  { id: "surprise", label: "Surprise", emoji: "😮", color: "#F7B125", asset: require("../assets/reactions/surprise.webp") },
  { id: "cake", label: "Cake", emoji: "🎂", color: "#E1306C", asset: require("../assets/reactions/cake.webp") },
  { id: "tada", label: "Celebrate", emoji: "🎉", color: "#F7B125", asset: require("../assets/reactions/tada.webp") },
  { id: "clap", label: "Clap", emoji: "👏", color: "#F7B125", asset: require("../assets/reactions/clap.webp") },
  { id: "fire", label: "Fire", emoji: "🔥", color: "#E9710F", asset: require("../assets/reactions/fire.webp") },
  { id: "party", label: "Party", emoji: "🥳", color: "#F7B125", asset: require("../assets/reactions/party.webp") },
];

export const REACTION_IDS = REACTIONS.map((r) => r.id);
export const emptyReactionCounts = (): ReactionCounts =>
  Object.fromEntries(REACTION_IDS.map((id) => [id, 0])) as ReactionCounts;

export type ReactionType = (typeof REACTIONS)[number]["id"] | null;
export type ReactionCounts = Record<Exclude<ReactionType, null>, number>;

export type ReactToPostPayload = {
  postId: string;
  userId: string;
  newReaction: ReactionType;
  previousReaction: ReactionType;
  currentCounts: ReactionCounts;
  existingReactionId?: string;
  reactorName?: string;
  reactorProfileImageUrl?: string;
};

export const LIFE_EVENT_TYPES = [
  'Promotion', 'Education', 'Relationship', 'NewJob', 'Moved', 'General',
];
export type LifeEventType = (typeof LIFE_EVENT_TYPES)[number];

export type PostFile = {
  attachment_id?: string;
  url: string;
  mime_type?: string;
  file_name: string | undefined;
  file_size: number | null;
};

export type PostTag = {
  id: string;
  name: string;
};

export type Post = {
  feedBoostedUntil?: string | null;
  ItemId: string;
  content: string;
  authorSnapshot: {
    firstName: string;
    lastName: string;
    profileImageUrl?: string;
    userId: string;
  };
  mentionedUsers?: SelectableItem[];
  hashtags?: string[];
  commentCount: number;
  reactionCount: ReactionCounts;
  /** Sticker projection, written only by club-stickers-v1. */
  stickers?: import("../services/api/posts/engagement-functions").StickerPlacement[] | null;
  stickerCount?: number | null;
  userReaction?: ReactionType;
  visibility: VisibilityType;
  visibleToGroupIds?: string[];
  visibleToUserIds?: string[];
  /**
   * The group a post was created *inside*, as opposed to `visibleToGroupIds`,
   * which is the audience chosen from the main feed. `GET_POSTS` selects it and
   * `insertpost` writes it; the model simply never declared it, so visibility
   * checks silently read `undefined`.
   */
  groupId?: string | null;
  attachmentUrls?: string[];
  CreatedDate: string;
  CreatedBy: string;
  saveCount: number;
  lifeEventType?: LifeEventType;
  lifeEventDate?: string;
  /**
   * Set when this post is the feed mirror of a published news article. Its
   * presence IS the flag — the card renders a "News" banner and taps through to
   * `/news/news-detail?newsId=…` instead of opening the post viewer. `newsTitle`
   * rides along so the banner can show the headline without fetching the
   * article. See `news-post.utils` and `news-post.service`.
   */
  newsId?: string | null;
  newsTitle?: string | null;
};

export type CommentAuthorSnapshot = {
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
};
export type PostComment = {
  ItemId: string;
  postId: string;
  parentCommentId?: string | null;

  content: string;
  replyCount: number;

  authorId: string;
  authorSnapshot: CommentAuthorSnapshot;
  reactionCount: Partial<Record<Exclude<ReactionType, null>, number>>;
  mentionedUsers?: SelectableItem[];

  CreatedDate: string;
  CreatedBy?: string;
};

export type PostCommentsResponse = PostComment[];

export type CreateCommentPayload = {
  postId: string;
  parentCommentId?: string;
  parentReplyCount?: number;
  postCommentCount?: number;
  authorId: string;
  content: string;
  authorSnapshot: {
    firstName?: string;
    lastName?: string;
    email?: string;
    profileImageUrl?: string;
  };
  replyCount: number;
  mentionedUsers?: SelectableItem[];
};

export type CreateReplyPayload = {
  commentId: string;
  body: string;
};

export type CreatePostPayload = {
  content: string;
  hashtags?: string[];
  mentionedUsers?: SelectableItem[];
  visibility?: VisibilityType;
  groupId?: string;
  visibleToGroupIds?: string[];
  visibleToUserIds?: string[];
  score: number;
  authorSnapshot: {
    firstName: string;
    lastName: string;
    email: string;
    profileImageUrl?: string;
  };
  attachmentUrls?: string[];
  lifeEventType?: LifeEventType;
  lifeEventDate?: string;
  /** Set only by the news mirror (see `news-post.service`), never by the composer. */
  newsId?: string;
  newsTitle?: string;
};

export type UpdatePostPayload = {
  id: string;
  content: string;
  hashtags?: string[];
  mentionedUsers?: SelectableItem[];
  visibility?: VisibilityType;
  visibleToGroupIds?: string[];
  visibleToUserIds?: string[];
  attachmentUrls?: string[];
  /**
   * Life-update fields. Editing a life-update post used to drop these entirely:
   * UPDATE_POST never carried them, so the category and date could not be changed
   * once set. (They were not *wiped* — GraphQL omits unprovided variables — which
   * is why this read as "cannot be updated" rather than as data loss.)
   */
  lifeEventType?: LifeEventType | null;
  lifeEventDate?: string | null;
  /** Re-sent when an article is edited so the mirror's headline stays in step. */
  newsTitle?: string | null;
};

export type UpdatePostScorePayload = {
  id: string;
  score: number;
};

export type ReactToCommentPayload = {
  commentId: string;
  postId: string;
  userId: string;
  newReaction: ReactionType | null;
  previousReaction: ReactionType | null;
  currentCounts: Partial<Record<Exclude<ReactionType, null>, number>>;
  existingReactionId?: string | null;
};

export type GetPostsResponse = {
  posts: Post[];
  meta: {
    page: number;
    last: number;
    next?: number;
    next_url?: string;
    count: number;
    limit: number;
  };
};

export type GetTagsResponse = {
  tags: string[];
};

export type UserPhoto = {
  id: string;
  file_name: string;
  file_size: number | null;
  mime_type: string;
  url: string;
};

export type GetUserPhotosResponse = {
  data: UserPhoto[];
  meta: {
    page: number;
    last: number;
    count: number;
  };
};

export type GetUserDocumentsResponse = {
  data: UserPhoto[];
  meta: {
    page: number;
    last: number;
    count: number;
  };
};
